/**
 * Staun Video Modal
 * Opens a modal with a YouTube/Vimeo embed or self-hosted video.
 * Loaded with defer from staun-video-cta section.
 */

'use strict';

(() => {
  const playButtons = document.querySelectorAll('[data-video-url], [data-video-src]');
  if (!playButtons.length) return;

  /**
   * Parse a YouTube or Vimeo URL into an embeddable URL.
   * @param {string} url
   * @returns {string|null}
   */
  const getEmbedUrl = (url) => {
    let match;

    // YouTube: various formats
    match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
    if (match) return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;

    // Vimeo
    match = url.match(/vimeo\.com\/(\d+)/);
    if (match) return `https://player.vimeo.com/video/${match[1]}?autoplay=1`;

    return null;
  };

  /**
   * Create and open the video modal.
   * @param {string} videoUrl - YouTube/Vimeo URL
   * @param {string} videoSrc - Self-hosted video source
   * @param {string} aspect   - "portrait" for 9:16 reel sizing, otherwise default 16:9
   */
  const openModal = (videoUrl, videoSrc, aspect = '') => {
    const modal = document.createElement('div');
    modal.className = 'staun-video-modal';
    if (aspect === 'portrait') {
      modal.classList.add('staun-video-modal--portrait');
    }
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Video player');

    const inner = document.createElement('div');
    inner.className = 'staun-video-modal__inner';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'staun-video-modal__close';
    closeBtn.setAttribute('aria-label', 'Close video');
    closeBtn.innerHTML = '&#10005;';

    let player;

    if (videoUrl) {
      const embedUrl = getEmbedUrl(videoUrl);
      if (embedUrl) {
        player = document.createElement('iframe');
        player.src = embedUrl;
        player.setAttribute('allowfullscreen', '');
        player.setAttribute('allow', 'autoplay; fullscreen');
      }
    }

    if (!player && videoSrc) {
      player = document.createElement('video');
      player.src = videoSrc;
      player.autoplay = true;
      player.controls = true;
      player.playsInline = true;
    }

    if (!player) return;

    inner.appendChild(closeBtn);
    inner.appendChild(player);
    modal.appendChild(inner);
    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    // Trigger transition
    requestAnimationFrame(() => {
      modal.classList.add('staun-video-modal--open');
    });

    const closeModal = () => {
      modal.classList.remove('staun-video-modal--open');
      modal.addEventListener('transitionend', () => {
        modal.remove();
        document.body.style.overflow = '';
      }, { once: true });
    };

    closeBtn.addEventListener('click', closeModal);

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    document.addEventListener('keydown', function handler(e) {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handler);
      }
    });
  };

  playButtons.forEach((btn) => {
    // Guard against double-binding if multiple sections on the same page each
    // include this script (the script is loaded with defer, so it only runs
    // once per src — but a Section Rendering API re-render of one section
    // could re-run a fresh copy. This flag keeps listeners idempotent.)
    if (btn.dataset.staunVideoModalReady === '1') return;
    btn.dataset.staunVideoModalReady = '1';

    btn.addEventListener('click', () => {
      const videoUrl = btn.dataset.videoUrl || '';
      const videoSrc = btn.dataset.videoSrc || '';
      const aspect = btn.dataset.aspectRatio || '';
      openModal(videoUrl, videoSrc, aspect);
    });
  });
})();
